import { z } from "zod";

export const roleSchema = z.enum(["parent", "child"]);
export type Role = z.infer<typeof roleSchema>;

export const themePrefSchema = z.enum(["system", "light", "dark"]);
export type ThemePref = z.infer<typeof themePrefSchema>;

export const treasureRewardIdSchema = z.enum([
  "fox-companion",
  "cat-companion",
  "dog-companion",
  "chicken-companion",
  "tiger-companion",
  "explorer-backpack",
  "school-backpack",
  "weekend-backpack",
  "hiking-backpack",
  "travel-backpack",
  "round-glasses",
  "dark-sunglasses",
  "red-sunglasses",
  "mirror-glasses",
  "cat-eye-glasses",
  "dollar-glasses",
  "sky-hoodie",
  "red-hoodie",
  "black-hoodie",
  "berry-hoodie",
  "orange-hoodie",
  "gold-dollar-chain",
  "silver-star-chain",
  "clover-chain",
  "blue-mohawk",
  "berry-mohawk",
  "sunny-mohawk",
  "leaf-aura",
  "star-aura",
  "rainbow-aura",
  "meadow-scene",
  "cozy-room-scene",
  "treehouse-room-scene",
  "space-room-scene",
  "club-room-scene",
]);
export type TreasureRewardId = z.infer<typeof treasureRewardIdSchema>;

export const treasureSlotSchema = z.enum([
  "companion",
  "backpack",
  "glasses",
  "hoodie",
  "necklace",
  "hair",
  "aura",
  "scene",
]);
export type TreasureSlot = z.infer<typeof treasureSlotSchema>;

export const treasureLoadoutSchema = z.object({
  companion: treasureRewardIdSchema.optional(),
  backpack: treasureRewardIdSchema.optional(),
  glasses: treasureRewardIdSchema.optional(),
  hoodie: treasureRewardIdSchema.optional(),
  necklace: treasureRewardIdSchema.optional(),
  hair: treasureRewardIdSchema.optional(),
  aura: treasureRewardIdSchema.optional(),
  scene: treasureRewardIdSchema.optional(),
});
export type TreasureLoadout = z.infer<typeof treasureLoadoutSchema>;

export const treasurePrefsSchema = z.object({
  openedChestCount: z.number().int().nonnegative(),
  ownedRewardIds: z.array(treasureRewardIdSchema),
  equipped: treasureLoadoutSchema.optional(),
});
export type TreasurePrefs = z.infer<typeof treasurePrefsSchema>;

export const userPrefsSchema = z.object({
  // Hex (#rrggbb), opcjonalne — fallback na domyślny akcent z CSS.
  accent: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  theme: themePrefSchema.optional(),
  // Mapa "context:variant" → ISO timestamp. Trzyma stan jakie mascot moments
  // już użytkownik widział, żeby nie pokazywać tego samego wielokrotnie.
  // Klucze np.: "level-up:5", "badge:streak-7", "welcome:1", "blog:{slug}".
  // Persistuje między urządzeniami (sync przez Firestore).
  seenMoments: z.record(z.string(), z.string()).optional(),
  // Mapa "context:variant" → ISO timestamp. Trzyma stan kliknięć w linki
  // zewnętrzne / promocyjne (analog seenMoments ale dla CTR). Klucze np.:
  // "blog:{slug}", "philosophy-link". Używane przez panel admin do liczenia
  // ile osób klika w blog (vs ile go widziało).
  clickedMoments: z.record(z.string(), z.string()).optional(),
  // Czy maskotka rodzica może promować artykuły z sianko.pl/blog
  // (raz na ~7 dni, w bubble na hero ParentDashboard). Default true.
  blogPromos: z.boolean().optional(),
  // Wyłącznie kosmetyczny stan Polany. Nie wpływa na XP, saldo ani
  // uprawnienia; źródłem skrzynek są approved completions ze zdjęciem.
  treasure: treasurePrefsSchema.optional(),
});
export type UserPrefs = z.infer<typeof userPrefsSchema>;

export const userDocSchema = z.object({
  uid: z.string(),
  email: z.string().email().nullable(),
  householdId: z.string(),
  role: roleSchema,
  // Per-user preferencje UI (motyw, kolor akcentu) — synchronizowane między urządzeniami.
  prefs: userPrefsSchema.optional(),
  /**
   * Sianko-level super admin (właściciel aplikacji, nie household admin).
   * Daje dostęp do `/admin` route + Cloud Functions `adminStats` /
   * `adminHouseholdDetail` (cross-HH agregaty: DAU/WAU, completions per
   * status, blog CTR, retencja, lista wszystkich HH).
   *
   * Ustawiane WYŁĄCZNIE przez `functions/scripts/grant-super-admin.mjs`
   * (Admin SDK omija rules). Klient nie może self-set — rules `users/{uid}`
   * update whitelist'ują tylko 'prefs', a `superAdmin` jest top-level.
   * Czyli polityka jest taka sama jak dla `householdId` / `role` —
   * immutable z poziomu klienta po jednorazowym set'cie przez Admin SDK.
   */
  superAdmin: z.boolean().optional(),
});
export type UserDoc = z.infer<typeof userDocSchema>;
